import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, Loader2, Target, TrendingUp } from 'lucide-react';
import type { ReliableAnalogPicksPayload } from '@/services/api';
import { useReliablePicksRanked } from '@/hooks/useReliablePicksRanked';
import { formatPct, pctChipClass } from './AnalogLensAccordion';
import { ReliablePickSelfAnalogDetail } from './ReliablePickSelfAnalogDetail';

type Props = {
  picks: ReliableAnalogPicksPayload;
  embedded?: boolean;
  className?: string;
};

export function ReliableAnalogPicksPanel({ picks, embedded = false, className = '' }: Props) {
  const [showAnalogs, setShowAnalogs] = useState(false);
  const baseRows = picks.picks ?? [];
  const referenceDate = picks.reference_date;
  const { sortedRows, strengthBySymbol, snapshotsBySymbol, loading } =
    useReliablePicksRanked(baseRows, referenceDate);
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

  if (!baseRows.length) return null;

  const rows = loading ? baseRows : sortedRows;
  const rule = picks.rule;
  const thin = rows.length < 15;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.08 }}
      className={
        embedded
          ? `border-b border-gray-100 bg-white ${className}`
          : `overflow-hidden rounded-3xl border border-gray-200 bg-white ${className}`
      }
      aria-label="Reliable analog stock picks"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className={embedded ? 'px-6 py-5 md:px-8' : 'border-b border-gray-100 px-6 py-5 md:px-8'}
      >
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.14 }}
          className="mb-2 flex flex-wrap items-center gap-2"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-700">
            <Target className="h-4 w-4 text-white" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
            Regime Stock Picks
          </span>
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
            {rows.length} names
          </span>
          {picks.analog_count != null ? (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              {picks.analog_count} analogs
            </span>
          ) : null}
        </motion.div>

        <motion.h3
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.18 }}
          className={`font-semibold tracking-tight text-gray-900 ${embedded ? 'text-base' : 'text-xl'}`}
        >
          Stocks that reliably rose after similar market days
        </motion.h3>

        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.22 }}
          className="mt-2 max-w-3xl text-sm leading-relaxed text-gray-500"
        >
          Equal-weight basket idea for the next{' '}
          <span className="font-medium text-gray-700">1–5 sessions</span>. Each name was green on ≥
          {rule?.min_hit_rate ?? 80}% of historical analogs with median 5-day move ≥
          {rule?.min_median_pct ?? 1}%. Click a row to expand self-analog detail inline.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.26 }}
          className="mt-3 flex flex-wrap gap-2"
        >
          <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600">
            Hit ≥{rule?.min_hit_rate ?? 80}%
          </span>
          <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600">
            Median 5d ≥ +{rule?.min_median_pct ?? 1}%
          </span>
          <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600">
            Lookback {picks.min_date?.slice(0, 4) ?? '2015'}+
          </span>
          {loading ? (
            <span className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              Ranking by self-analog strength…
            </span>
          ) : (
            <span className="rounded-lg bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-800">
              Sorted strongest → weakest
            </span>
          )}
        </motion.div>

        {thin ? (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            Thin basket today — only {rows.length} names passed the filter. Treat as selective, not
            a full 30-name list.
          </motion.p>
        ) : null}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.28 }}
        className={embedded ? 'px-6 pb-5 md:px-8' : 'p-4 md:p-6'}
      >
        <div className="overflow-hidden rounded-2xl border border-gray-200">
          <div className="hidden grid-cols-[2.5rem_5rem_1fr_5.5rem_5.5rem_3.5rem] gap-3 border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 sm:grid">
            <span>#</span>
            <span>Symbol</span>
            <span />
            <span className="text-right">Regime hit</span>
            <span className="text-right">Regime 5d</span>
            <span className="text-right">Self 5d</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {rows.map((row, index) => {
              const expanded = selectedSymbol === row.symbol;
              const self = strengthBySymbol[row.symbol];
              return (
                <li key={row.symbol}>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedSymbol((cur) => (cur === row.symbol ? null : row.symbol))
                    }
                    className={`grid w-full grid-cols-[2.5rem_1fr_auto] items-center gap-3 px-4 py-3 text-left transition-colors sm:grid-cols-[2.5rem_5rem_1fr_5.5rem_5.5rem_3.5rem] ${
                      expanded ? 'bg-emerald-50/70' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-1 text-xs font-medium tabular-nums text-gray-400">
                      {index + 1}
                      <ChevronDown
                        className={`h-3.5 w-3.5 text-gray-400 transition-transform ${
                          expanded ? 'rotate-180 text-emerald-700' : ''
                        }`}
                      />
                    </span>
                    <span
                      className={`text-sm font-semibold ${
                        expanded ? 'text-emerald-900' : 'text-gray-900'
                      }`}
                    >
                      {row.symbol}
                    </span>
                    <div className="hidden min-w-0 sm:block">
                      {self?.hit5 != null ? (
                        <span className="text-xs text-gray-400">
                          Self {self.hit5.toFixed(0)}% hit
                          {row.median_excess_pct != null
                            ? ` · vs SPY ${formatPct(row.median_excess_pct)}`
                            : ''}
                        </span>
                      ) : row.median_excess_pct != null ? (
                        <span className="text-xs text-gray-400">
                          vs SPY med {formatPct(row.median_excess_pct)}
                        </span>
                      ) : loading ? (
                        <span className="text-xs text-gray-300">Ranking…</span>
                      ) : null}
                    </div>
                    <span className="text-right text-sm font-medium tabular-nums text-gray-700">
                      {row.hit_rate != null ? `${row.hit_rate.toFixed(0)}%` : '—'}
                    </span>
                    <span
                      className={`text-right text-sm font-semibold tabular-nums ${pctChipClass(row.median_pct ?? 0)}`}
                    >
                      {row.median_pct != null ? formatPct(row.median_pct) : '—'}
                    </span>
                    <span
                      className={`text-right text-sm font-semibold tabular-nums ${
                        self?.med5 != null ? pctChipClass(self.med5) : 'text-gray-400'
                      }`}
                    >
                      {loading && !self ? (
                        '…'
                      ) : self?.med5 != null ? (
                        formatPct(self.med5)
                      ) : (
                        '—'
                      )}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {expanded ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden"
                      >
                        <ReliablePickSelfAnalogDetail
                          symbol={row.symbol}
                          referenceDate={referenceDate}
                          preloadedSnapshot={snapshotsBySymbol[row.symbol]}
                          inline
                        />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {rows.slice(0, 12).map((row) => {
            const expanded = selectedSymbol === row.symbol;
            return (
              <button
                key={`chip-${row.symbol}`}
                type="button"
                onClick={() =>
                  setSelectedSymbol((cur) => (cur === row.symbol ? null : row.symbol))
                }
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  expanded
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <TrendingUp
                  className={`h-3 w-3 ${expanded ? 'text-emerald-700' : 'text-emerald-600'}`}
                />
                {row.symbol}
              </button>
            );
          })}
          {rows.length > 12 ? (
            <span className="self-center text-xs text-gray-400">+{rows.length - 12} more</span>
          ) : null}
        </div>


        {picks.analog_dates?.length ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45 }}
            className="mt-4"
          >
            <button
              type="button"
              onClick={() => setShowAnalogs((v) => !v)}
              className="flex w-full items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-left text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
            >
              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform ${showAnalogs ? 'rotate-180' : ''}`}
              />
              Top-{picks.analog_count ?? picks.analog_dates.length} market analog dates used
            </button>
            <AnimatePresence initial={false}>
              {showAnalogs ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <p className="px-1 pt-2 text-xs leading-relaxed text-gray-400">
                    {picks.analog_dates.join(' · ')}
                  </p>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        ) : null}
      </motion.div>

      {picks.disclaimer ? (
        <p
          className={`text-xs leading-relaxed text-gray-400 ${
            embedded ? 'px-6 pb-5 md:px-8' : 'border-t border-gray-100 px-6 py-4 md:px-8'
          }`}
        >
          {picks.disclaimer}
        </p>
      ) : null}
    </motion.section>
  );
}
